import {
  useState, useCallback, useMemo, memo,
} from 'react';
import { motion } from 'framer-motion';
import { useInvite } from '@/hooks/useInvite';
import { useToastContext } from '@/hooks/useToastContext';
import { TEMPLATES, DEFAULT_TEMPLATE } from '@/constants/templates';
import { validateInviteForm } from '@/utils/validation';
import type { CreateInviteFormData } from '@/types/invite';
import type { CreateInviteRequest } from '@/types/api';
import styles from './CreateInviteForm.module.css';

type CreateInviteFormProps = {
  onSuccess: (inviteId: string) => void;
};

/**
 * Форма создания приглашения
 *
 * Отправляет данные на API endpoint /create-invite
 * После успешного создания вызывает onSuccess с ID приглашения
 */
const CreateInviteForm = memo(({ onSuccess }: CreateInviteFormProps) => {
  const { createInvite, loading, error } = useInvite();
  const { showError } = useToastContext();

  const [formData, setFormData] = useState<CreateInviteFormData>({
    brideName: '',
    groomName: '',
    weddingDate: '',
    weddingLocation: '',
    template: DEFAULT_TEMPLATE,
  });

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // Валидация формы
    const validation = validateInviteForm(formData);
    if (!validation.success) {
      return;
    }

    // Подготавливаем данные для отправки
    const submitData: CreateInviteRequest = {
      ...validation.data!,
    };

    const result = await createInvite(submitData);

    if (result.data && !result.error) {
      onSuccess(result.data.inviteId);
    } else if (result.error) {
      showError(result.error);
    }
  }, [formData, createInvite, onSuccess, showError]);

  const isFormValid = useMemo(() => formData.brideName.trim() !== ''
      && formData.groomName.trim() !== ''
      && formData.weddingDate !== ''
      && formData.weddingLocation.trim() !== '', [formData]);

  return (
    <motion.form
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onSubmit={handleSubmit}
      className={styles.form}
    >
      <div className={styles.formGroup}>
        <label htmlFor="template" className={styles.label}>
          Шаблон приглашения
          <select
            id="template"
            name="template"
            value={formData.template || DEFAULT_TEMPLATE}
            onChange={handleChange}
            className={styles.input}
          >
            {TEMPLATES.map((template) => (
              <option key={template.value} value={template.value}>
                {template.label}
                {' '}
                -
                {template.description}
              </option>
            ))}
          </select>
        </label>
        <p className={styles.helpText}>
          Выберите дизайн для вашего приглашения. Шаблон можно изменить позже.
        </p>
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="brideName" className={styles.label}>
          Имя невесты
          <input
            type="text"
            id="brideName"
            name="brideName"
            value={formData.brideName}
            onChange={handleChange}
            required
            className={styles.input}
            placeholder="Введите имя невесты"
          />
        </label>
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="groomName" className={styles.label}>
          Имя жениха
          <input
            type="text"
            id="groomName"
            name="groomName"
            value={formData.groomName}
            onChange={handleChange}
            required
            className={styles.input}
            placeholder="Введите имя жениха"
          />
        </label>
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="weddingDate" className={styles.label}>
          Дата свадьбы
          <input
            type="date"
            id="weddingDate"
            name="weddingDate"
            value={formData.weddingDate}
            onChange={handleChange}
            required
            className={styles.input}
          />
        </label>
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="weddingLocation" className={styles.label}>
          Место проведения
          <input
            type="text"
            id="weddingLocation"
            name="weddingLocation"
            value={formData.weddingLocation}
            onChange={handleChange}
            required
            className={styles.input}
            placeholder="Адрес или название места"
          />
        </label>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={styles.error}
        >
          {error}
        </motion.div>
      )}

      <motion.button
        type="submit"
        disabled={loading || !isFormValid}
        whileHover={!loading && isFormValid ? { scale: 1.02 } : {}}
        whileTap={!loading && isFormValid ? { scale: 0.98 } : {}}
        className={styles.submitButton}
      >
        {loading ? 'Создание...' : 'Создать приглашение'}
      </motion.button>
    </motion.form>
  );
});

CreateInviteForm.displayName = 'CreateInviteForm';

export default CreateInviteForm;
